import { foldDiacritics } from "@/lib/tool-kit/ciphers/alphabet";
import type { CipherDirection } from "@/lib/tool-kit/ciphers-polygraphic/keyed-letters";

/**
 * The trifid cipher (Félix Delastelle).
 *
 * Bifid's three-dimensional cousin. The 26 letters and one extra symbol fill a
 * 3 × 3 × 3 cube — three layers, each with three rows and three columns — so every
 * symbol has three coordinates, each 1, 2 or 3. Write the message's layer numbers on one
 * line, the row numbers beneath, and the column numbers beneath those; read the digits
 * across (all the layers, then all the rows, then all the columns) and cut the stream into
 * triples. Each triple names a cipher symbol in the cube.
 *
 * Messages are cut into blocks of `period` symbols (0 = the whole message), each done on
 * its own. The keyword is written into the cube first, then the rest of the alphabet and
 * the extra symbol, layer by layer. Delastelle's extra symbol was "+"; the American
 * Cryptogram Association uses "#"; a full stop is also seen. It is selectable.
 */

export type TrifidSymbol = "+" | "#" | ".";

export const CUBE_SIDE = 3;
const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

export interface TrifidOptions {
  keyword: string;
  /** The 27th symbol of the cube. */
  symbol: TrifidSymbol;
  /** Symbols per block; 0 for the whole message as one block. */
  period: number;
}

export interface TrifidBlock {
  plain: string;
  cipher: string;
  /** Layer, row and column of each plain symbol, 1-based. */
  layers: number[];
  rows: number[];
  columns: number[];
  /** Layers, then rows, then columns: the stream the cipher symbols are read from. */
  stream: number[];
}

export interface TrifidResult {
  /** The 27 symbols, layer by layer, row by row. */
  cube: string;
  blocks: TrifidBlock[];
  output: string;
}

/** The symbols a cube holds, in plain order: A–Z and the extra symbol. */
export function cubeAlphabet(symbol: TrifidSymbol): string {
  return `${LETTERS}${symbol}`;
}

function cubeChars(text: string, symbol: TrifidSymbol): string {
  const alphabet = cubeAlphabet(symbol);
  let chars = "";
  for (const char of foldDiacritics(text).toUpperCase()) {
    if (alphabet.includes(char)) {
      chars += char;
    }
  }
  return chars;
}

/** The cube as 27 symbols: the keyword first, each symbol once, then the rest in order. */
export function buildCube(keyword: string, symbol: TrifidSymbol): string {
  const used = new Set<string>();
  let cube = "";
  for (const char of `${cubeChars(keyword, symbol)}${cubeAlphabet(symbol)}`) {
    if (!used.has(char)) {
      used.add(char);
      cube += char;
    }
  }
  return cube;
}

/** Layer, row and column (1-based) of a symbol in the cube. */
export function cubePoint(
  cube: string,
  char: string
): { layer: number; row: number; column: number } {
  const index = cube.indexOf(char);
  const face = CUBE_SIDE * CUBE_SIDE;
  return {
    layer: Math.floor(index / face) + 1,
    row: Math.floor((index % face) / CUBE_SIDE) + 1,
    column: (index % CUBE_SIDE) + 1,
  };
}

function symbolAt(cube: string, layer: number, row: number, column: number) {
  const face = CUBE_SIDE * CUBE_SIDE;
  return cube.charAt((layer - 1) * face + (row - 1) * CUBE_SIDE + (column - 1));
}

function splitBlocks(chars: string, period: number): string[] {
  if (period < 1 || period >= chars.length) {
    return chars === "" ? [] : [chars];
  }
  const blocks: string[] = [];
  for (let start = 0; start < chars.length; start += period) {
    blocks.push(chars.slice(start, start + period));
  }
  return blocks;
}

function coordinates(cube: string, chars: string) {
  const layers: number[] = [];
  const rows: number[] = [];
  const columns: number[] = [];
  for (const char of chars) {
    const point = cubePoint(cube, char);
    layers.push(point.layer);
    rows.push(point.row);
    columns.push(point.column);
  }
  return { layers, rows, columns };
}

function encipherBlock(cube: string, plain: string): TrifidBlock {
  const { layers, rows, columns } = coordinates(cube, plain);
  const stream = [...layers, ...rows, ...columns];
  let cipher = "";
  for (let index = 0; index < stream.length; index += 3) {
    cipher += symbolAt(
      cube,
      stream[index],
      stream[index + 1],
      stream[index + 2]
    );
  }
  return { plain, cipher, layers, rows, columns, stream };
}

function decipherBlock(cube: string, cipher: string): TrifidBlock {
  const first = coordinates(cube, cipher);
  const stream: number[] = [];
  for (const [index, layer] of first.layers.entries()) {
    stream.push(layer, first.rows[index], first.columns[index]);
  }
  const size = cipher.length;
  const layers = stream.slice(0, size);
  const rows = stream.slice(size, size * 2);
  const columns = stream.slice(size * 2);
  let plain = "";
  for (const [index, layer] of layers.entries()) {
    plain += symbolAt(cube, layer, rows[index], columns[index]);
  }
  return { plain, cipher, layers, rows, columns, stream };
}

export function trifidTransform(
  text: string,
  options: TrifidOptions,
  direction: CipherDirection
): TrifidResult {
  const cube = buildCube(options.keyword, options.symbol);
  const chars = cubeChars(text, options.symbol);
  const blocks = splitBlocks(chars, options.period).map((block) =>
    direction === "encode"
      ? encipherBlock(cube, block)
      : decipherBlock(cube, block)
  );
  const output = blocks
    .map((block) => (direction === "encode" ? block.cipher : block.plain))
    .join("");
  return { cube, blocks, output };
}
